import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { confirmAlert, Tooltip } from 'components/common';
import RichContent from 'components/common/RichContent';
import { escapeHtml } from 'helpers/utils';
import { LuPhone, LuMail, LuMessageSquare, LuMoreHorizontal, LuCalendar, LuCheckSquare,
         LuPencil, LuTrash2, LuCheck, LuClock, LuRefreshCw } from 'react-icons/lu';
import {
  deleteActivity as _deleteActivity, deleteTask as _deleteTask,
  updateActivity as _updateActivity, updateTask as _updateTask,
} from 'redux/crm/project/projectActivitySlice';
import ActivityAttachments from './ActivityAttachments';
import { fmtDate, fmtDateTime, fmtEventTime, fmtOverdueAge, fmtWeekday, eventDateOf, isDone as isDoneItem } from './activityUtils';

// Icon + colour per kind. The node colour is the row's only decoration, so the
// type is readable at a glance without a separate chip competing with the title.
const KIND_CFG = {
  meeting: { Icon: LuCalendar,        label: 'Meeting', ring: 'tw-bg-indigo-50 tw-text-indigo-600 tw-ring-indigo-100' },
  call:    { Icon: LuPhone,           label: 'Call',    ring: 'tw-bg-emerald-50 tw-text-emerald-600 tw-ring-emerald-100' },
  email:   { Icon: LuMail,            label: 'Email',   ring: 'tw-bg-sky-50 tw-text-sky-600 tw-ring-sky-100' },
  sms:     { Icon: LuMessageSquare,   label: 'SMS',     ring: 'tw-bg-violet-50 tw-text-violet-600 tw-ring-violet-100' },
  other:   { Icon: LuMoreHorizontal,  label: 'Other',   ring: 'tw-bg-slate-100 tw-text-slate-500 tw-ring-slate-200' },
  task:    { Icon: LuCheckSquare,     label: 'Task',    ring: 'tw-bg-amber-50 tw-text-amber-600 tw-ring-amber-100' },
};

const PRIORITY_CFG = {
  low:    { label: 'Low',    cls: 'tw-text-sky-700 tw-bg-sky-50 tw-border-sky-200',       dot: 'tw-bg-sky-400' },
  medium: { label: 'Medium', cls: 'tw-text-amber-700 tw-bg-amber-50 tw-border-amber-200', dot: 'tw-bg-amber-400' },
  high:   { label: 'High',   cls: 'tw-text-red-700 tw-bg-red-50 tw-border-red-200',       dot: 'tw-bg-red-400' },
};

const STATUS_CFG = {
  todo:        { label: 'To do',       cls: 'tw-text-slate-600 tw-bg-slate-100 tw-border-slate-200' },
  in_progress: { label: 'In progress', cls: 'tw-text-primary-700 tw-bg-primary-50 tw-border-primary-200' },
  done:        { label: 'Done',        cls: 'tw-text-emerald-700 tw-bg-emerald-50 tw-border-emerald-200' },
  cancelled:   { label: 'Cancelled',   cls: 'tw-text-slate-500 tw-bg-slate-100 tw-border-slate-200' },
};

const PILL = 'tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-border tw-text-[11px] tw-font-semibold tw-whitespace-nowrap';

// Hover-revealed on pointer devices, always shown on touch (no hover state there).
const ACTION_BTN = 'tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-lg tw-border tw-border-transparent tw-bg-transparent tw-cursor-pointer tw-transition-colors';

const ActivityItem = ({
  item, onEdit, projectId, isFirst, isLast, showDate,
  // Action overrides let the contact/company panel reuse this row against the
  // recipient endpoints instead of the project ones.
  updateActivity = _updateActivity, deleteActivity = _deleteActivity,
  updateTask     = _updateTask,     deleteTask     = _deleteTask,
}) => {
  const dispatch = useDispatch();

  const isTask    = item._type === 'task';
  const done      = isDoneItem(item);
  const isOverdue = !!item.overdue && !done;

  // Permission gates from the API — default to allowed for older cached records.
  const canEdit   = item.permissions?.allow_edit   ?? true;
  const canDelete = item.permissions?.allow_delete ?? true;

  const kind    = KIND_CFG[isTask ? 'task' : (item.activity_type || 'other')] || KIND_CFG.other;
  const Icon    = kind.Icon;
  const dateStr = fmtDate(eventDateOf(item));
  const timeStr = fmtEventTime(item);
  const body    = isTask ? item.description : (item.note || item.description);

  const status   = STATUS_CFG[item.status];
  const priority = isTask ? PRIORITY_CFG[item.priority] : null;

  const recipientId = projectId || item.recipient_id;

  const handleDelete = () => {
    confirmAlert({
      title:   `Delete ${isTask ? 'task' : 'activity'}`,
      message: `Are you sure you want to delete <strong>"${escapeHtml(item.name)}"</strong>? This action cannot be undone.`,
      type:    'danger',
      closeOnClickOutside: true,
      closeOnEscape:       true,
      buttons: [
        {
          label: 'Delete',
          onClick: async () => {
            const action = isTask
              ? deleteTask({ projectId: recipientId, id: item.id })
              : deleteActivity({ projectId: recipientId, id: item.id });
            const res = await dispatch(action);
            if (res.meta.requestStatus === 'fulfilled') toast.success('Deleted successfully.');
            else toast.error('Failed to delete. Please try again.');
          },
        },
        { label: 'Cancel', onClick: () => {} },
      ],
    });
  };

  const handleMarkDone = async () => {
    const action = isTask
      ? updateTask({ projectId: recipientId, id: item.id, data: { status: 'done' } })
      : updateActivity({ projectId: recipientId, id: item.id, data: { status: 'done' } });
    const res = await dispatch(action);
    if (res.meta.requestStatus !== 'fulfilled') toast.error('Failed to update. Please try again.');
  };

  return (
    <div
      className={`tw-group tw-relative tw-flex tw-gap-3 tw-px-4 tw-transition-colors
        tw-border-b tw-border-slate-100 last:tw-border-b-0
        ${done ? 'tw-bg-white' : 'hover:tw-bg-slate-50'}`}
    >
      {/* Date gutter — the date is printed once per day, times on every row */}
      <div className="tw-w-[5.5rem] tw-flex-shrink-0 tw-text-right tw-py-3 tw-leading-tight">
        {showDate && dateStr ? (
          <>
            <div className={`tw-text-xs tw-font-bold ${isOverdue ? 'tw-text-red-600' : 'tw-text-slate-700'}`}>
              {dateStr}
            </div>
            <div className="tw-text-[10px] tw-font-medium tw-text-slate-400 tw-mt-0.5">
              {fmtWeekday(eventDateOf(item))}
            </div>
          </>
        ) : null}
        {timeStr ? (
          <div className={`tw-text-[11px] tw-font-medium tw-mt-0.5 ${isOverdue ? 'tw-text-red-400' : 'tw-text-slate-400'}`}>
            {timeStr}
          </div>
        ) : null}
      </div>

      {/* Timeline rail: a stub above and a stub below the node, trimmed at the ends.
          The column stretches over the whole row so the rail bridges the gap
          between one node and the next. */}
      <div className="tw-flex tw-flex-col tw-items-center tw-flex-shrink-0 tw-w-8 tw-self-stretch">
        <span aria-hidden className={`tw-w-px tw-h-3 ${isFirst ? '' : 'tw-bg-slate-200'}`} />
        <span
          className={`tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded-full tw-ring-1 tw-flex-shrink-0
            ${done ? 'tw-bg-slate-100 tw-text-slate-400 tw-ring-slate-200' : kind.ring}`}
          title={kind.label}
        >
          <Icon size={14} />
        </span>
        <span aria-hidden className={`tw-w-px tw-flex-1 ${isLast ? '' : 'tw-bg-slate-200'}`} />
      </div>

      {/* Content */}
      <div className="tw-flex-1 tw-min-w-0 tw-flex tw-items-start tw-justify-between tw-gap-3 tw-py-3">
        <div className="tw-flex-1 tw-min-w-0">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
            <span className={`tw-text-sm tw-font-semibold tw-leading-snug tw-break-words
              ${done ? 'tw-text-slate-400 tw-line-through' : 'tw-text-slate-800'}`}>
              {item.name}
            </span>

            {isOverdue && (
              <span className={`${PILL} tw-text-red-700 tw-bg-red-50 tw-border-red-200`}>
                <LuClock size={10} /> {fmtOverdueAge(item)}
              </span>
            )}

            {item.synced_from_opla && (
              <span
                className={`${PILL} tw-text-teal-700 tw-bg-teal-50 tw-border-teal-200`}
                title="Synced from Opla CRM"
              >
                <LuRefreshCw size={10} /> Opla
              </span>
            )}
          </div>

          {/* Meta line — kind, people, provenance */}
          <div className="tw-flex tw-items-center tw-gap-x-1.5 tw-gap-y-0.5 tw-flex-wrap tw-text-[11px] tw-text-slate-400 tw-mt-1">
            <span className="tw-font-semibold tw-text-slate-500">{isTask ? (item.task_type || kind.label) : kind.label}</span>
            {item.contact_name  && <><span aria-hidden>·</span><span>{item.contact_name}</span></>}
            {item.assignee_name && <><span aria-hidden>·</span><span>Assignee: {item.assignee_name}</span></>}
            <span aria-hidden>·</span>
            <span>{fmtDateTime(item.created_at)} by {item.created_by || '—'}</span>
          </div>

          {body && (
            <RichContent
              html={body}
              collapsible
              collapsedHeight={72}
              className="tw-text-[13px] tw-text-slate-600 tw-mt-1.5 tw-leading-relaxed"
            />
          )}

          <ActivityAttachments attachments={item.attachments} />
        </div>

        {/* Status + row actions */}
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0 tw-pt-0.5">
          {priority && !done && (
            <span className={`${PILL} ${priority.cls}`}>
              <span className={`tw-w-1.5 tw-h-1.5 tw-rounded-full ${priority.dot}`} />
              {priority.label}
            </span>
          )}
          {status && <span className={`${PILL} ${status.cls}`}>{status.label}</span>}

          <div className="tw-flex tw-items-center tw-gap-0.5 tw-opacity-100 md:tw-opacity-0 md:group-hover:tw-opacity-100 md:focus-within:tw-opacity-100 tw-transition-opacity">
            {!done && (
              <Tooltip content="Mark as done">
                <button type="button" aria-label="Mark as done" onClick={handleMarkDone}
                  className={`${ACTION_BTN} tw-text-slate-400 hover:tw-bg-emerald-50 hover:tw-text-emerald-600 hover:tw-border-emerald-200`}>
                  <LuCheck size={14} />
                </button>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip content="Edit">
                <button type="button" aria-label="Edit" onClick={() => onEdit(item)}
                  className={`${ACTION_BTN} tw-text-slate-400 hover:tw-bg-primary-50 hover:tw-text-primary-600 hover:tw-border-primary-200`}>
                  <LuPencil size={13} />
                </button>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip content="Delete">
                <button type="button" aria-label="Delete" onClick={handleDelete}
                  className={`${ACTION_BTN} tw-text-slate-400 hover:tw-bg-red-50 hover:tw-text-red-600 hover:tw-border-red-200`}>
                  <LuTrash2 size={13} />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ActivityItem.propTypes = {
  item:      PropTypes.object.isRequired,
  onEdit:    PropTypes.func.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Trim the timeline rail at the ends of a section. */
  isFirst:   PropTypes.bool,
  isLast:    PropTypes.bool,
  /** False for consecutive rows on the same day, so the date prints once. */
  showDate:  PropTypes.bool,
  updateActivity: PropTypes.func,
  deleteActivity: PropTypes.func,
  updateTask:     PropTypes.func,
  deleteTask:     PropTypes.func,
};

ActivityItem.defaultProps = {
  projectId: undefined,
  isFirst:   false,
  isLast:    false,
  showDate:  true,
};

export default ActivityItem;
